<template>
  <div class="min-h-[100svh] container py-[2svh] lg:py-[12svh] relative max-lg:flex max-lg:flex-col">
    <div class="flex gap-3 lg:hidden h-[6svh]" v-if="!gameEnd">
      <div :key="player" v-for="player in players"
           class=" relative rounded overflow-clip flex"
           :style="{width: 100/players.length + '%'}">
        <div class="bg-white grow w-1 leading-none p-3 h-full flex items-center overflow-hidden text-ellipsis whitespace-nowrap">
          {{ player.name }}
        </div>
        <div class="aspect-square flex items-center justify-center w-10 h-full bg-primary-dark text-center text-white text-lg leading-none">
          {{ player.totalScore }}
        </div>
      </div>
      <div @click="hideStats = false" class="lg:hidden rounded h-auto bg-success flex items-center justify-center text-white px-4 rounded-b">
        Stats
      </div>
    </div>
    <div class="default-grid grow" v-if="!gameEnd">
      <div class="col-span-full lg:col-span-7 relative max-lg:mt-3 flex flex-col">
        <div
            class="text-sm lg:text-lg relative lg:absolute lg:top-0 bg-primary-dark lg:-translate-y-full w-full rounded-t after:bg-primary-dark after:absolute after:inset-x-0 after:top-full after:h-10 text-white px-6 py-2 transition-[transform,opacity] duration-200">
          {{ currentPlayer.name }}'s turn!
        </div>

        <div class="grid grid-cols-5 sm:grid-cols-3 shadow-lg grow relative z-2 bg-white rounded overflow-clip">
          <div class="h-full flex flex-col" :key="players.find(p => p.id === thisUserId).id">
            <ul class="grow">
              <li v-html="getHitsIcon(players.find(p => p.id === thisUserId).hits[num] || 0)" :style="{height: (100/this.numbers.length) + '%'}"
                  class="text-xl flex items-center justify-center even:bg-[#FAFAFA]" v-for="num in numbers" :key="num"
                  :title="num"></li>
            </ul>
          </div>
          <div class="h-full text-lg lg:text-xl rounded-t max-sm:col-span-3">
            <div
                :class="{'pointer-events-none relative after:absolute after:bg-black/80 after:-inset-x-[100vw] after:-translate-y-1/2 after:top-1/2 after:h-0.5 after:rounded' : isClosed(num), 'grid-cols-3' : num !== 'Bull', 'grid-cols-2' : num === 'Bull', 'cursor-not-allowed': currentPlayer !== thisUserId}"
                :key="num" v-for="num in numbers" :style="{height: (100/this.numbers.length) + '%'}"
                class="px-5 grid even:bg-[#FAFAFA]">

              <button @click="play(num, 1)"
                      :class="{'opacity-50 cursor-not-allowed pointer-events-none': currentPlayer.id !== thisUserId}">
                <span class="relative">
                  {{ num }}
                  <span class="absolute left-1/2 -translate-x-1/2 -bottom-4 pr-[.2em] tracking-[-.2em]">.</span>
                </span>
              </button>
              <button class="relative" @click="play(num, 2)"
                      :class="{'opacity-50 cursor-not-allowed pointer-events-none': currentPlayer.id !== thisUserId}">
                <span class="relative">
                  {{ num }}
                  <span class="absolute left-1/2 -translate-x-1/2 -bottom-4 pr-[.2em] tracking-[-.2em]">..</span>
                </span>
              </button>
              <button v-if="num !== 'Bull'" @click="play(num, 3)"
                      :class="{'opacity-50 cursor-not-allowed pointer-events-none': currentPlayer.id !== thisUserId}">
                <span class="relative">
                  {{ num }}
                  <span class="absolute left-1/2 -translate-x-1/2 -bottom-4 pr-[.2em] tracking-[-.2em]">...</span>
                </span>
              </button>
            </div>
          </div>
          <div class="h-full flex flex-col" :key="players.find(p => p.id !== thisUserId).id">
            <ul class="grow">
              <li v-html="getHitsIcon(players.find(p => p.id !== thisUserId).hits[num] || 0)" :style="{height: (100/this.numbers.length) + '%'}"
                  class="text-xl flex items-center justify-center even:bg-[#FAFAFA]" v-for="num in numbers" :key="num"
                  :title="num"></li>
            </ul>
          </div>
        </div>
        <div
            class="lg:absolute bottom-0 inset-x-0 mx-auto w-[80%] lg:w-[47%] flex h-[6svh] shadow-lg transition-transform"
            :class="{'cursor-not-allowed lg:translate-y-full': currentPlayer.id === thisUserId || currentPlayer.bot}">
          <button
              class="bg-error text-white px-5 rounded-bl"
              :class="{'opacity-20 pointer-events-none': currentPlayer.id !== thisUserId && !currentPlayer.bot, 'opacity-70': history.length <= 1 && (currentPlayer.id === thisUserId || currentPlayer.id !== thisUserId && currentPlayer.bot)}"
              @click="undoPlayerAction">
            Undo
          </button>
          <!-- Disable Next button for non-current player -->
          <button
              class="bg-success text-white px-5 rounded-br grow"
              :class="{'opacity-20 pointer-events-none': currentPlayer.id !== thisUserId}"
              @click="nextPlayerAction">
            Next
          </button>
        </div>
      </div>

      <div
          class="max-lg:absolute max-lg:inset-0 max-lg:max-h-[100svh] max-lg:overflow-auto z-2 max-lg:bg-primary max-lg:p-8 max-lg:pt-16 max-lg:opacity-0 max-lg:invisible max-lg:duration-150 max-lg:transition-[opacity,visibility] col-span-full lg:col-span-5 gap-10 flex flex-col"
          :class="{'max-lg:!opacity-100 max-lg:!visible': !hideStats}">
        <div @click="hideStats = true"
             class="lg:hidden absolute top-0 right-0 bg-error flex items-center justify-center text-white px-4 py-2 rounded-bl">
          Close
        </div>
        <div v-for="player in players" :key="player.id" class="relative h-1/2 bg-white rounded shadow-lg">
          <div
              class="absolute -right-3 -top-3 lg:-right-4 lg:-top-4 aspect-square flex items-center justify-center size-14 lg:size-16 bg-primary-dark rounded-full text-center text-white text-xl lg:text-2xl leading-none">
            {{ player.totalScore }}
          </div>
          <div class="rounded relative overflow-clip pt-8 px-8 pb-12 h-full">
            <h3 class="text-lg font-bold !mt-0">{{ player.name }}
<!--              <span class="text-xs opacity-20 pointer-events-none">({{-->
<!--                player.id.substr(0, 6)-->
<!--              }})</span>-->
            </h3>
            <div class="leading-[2]">
              <p>Doubles: {{ player.doubles }}</p>
              <p>Triples: {{ player.triples }}</p>
              <p>Highest Score: {{ player.highestThreeDartScore }}</p>
              <p>Sets won: {{ player.setsWon }} / {{ gameSettings.setsToWin }}</p>
            </div>
            <div v-if="player.dartsThrown > 0" class="absolute bottom-0 inset-x-0 w-100 h-8">
              <div :title="'Hits: ' + player.dartsHit" v-text="player.dartsHit"
                   class="absolute left-0 inset-y-0 bg-success text-white overflow-clip flex items-center justify-center"
                   :style="{width: ((player.dartsHit / player.dartsThrown) * 100) + '%'}"></div>
              <div :title="'Misses: ' + (player.dartsThrown - player.dartsHit)"
                   v-text="player.dartsThrown - player.dartsHit"
                   class="absolute text-white right-0 inset-y-0 bg-error overflow-clip flex items-center justify-center"
                   :style="{width: ((1 - (player.dartsHit / player.dartsThrown)) * 100) + '%'}"></div>
            </div>
          </div>
        </div>
        <div class="bg-white rounded shadow-lg p-8">
          <h3 class="text-lg font-bold">Game Timer</h3>
          <p>{{ formatTime(timer) }}</p>
          <p>Turn: {{ round }}</p>
        </div>
      </div>
    </div>

    <div v-else-if="gameEnd" class="flex flex-col gap-6">
      <h2 v-if="winner !== '' && winner.name" class="text-white text-2xl text-center">🎉 {{ winner.name }} Wins! 🎉</h2>

      <div v-for="player in players" :key="player.id" class="relative h-1/2 bg-white rounded shadow-lg">
        <div
            class="absolute -right-3 -top-3 lg:-right-4 lg:-top-4 aspect-square flex items-center justify-center size-14 lg:size-16 bg-primary-dark rounded-full text-center text-white text-xl lg:text-2xl leading-none">
          {{ player.totalScore }}
        </div>
        <div class="rounded relative overflow-clip pt-8 px-8 pb-12 h-full">
          <h3 class="text-lg font-bold !mt-0">{{ player.name }} <span
              class="text-xs opacity-20 pointer-events-none" v-if="player.id">({{ player.id.substr(0, 6) }})</span></h3>
          <div class="leading-[2]">
            <p>Doubles: {{ player.doubles }}</p>
            <p>Triples: {{ player.triples }}</p>
            <p>Highest Score: {{ player.highestThreeDartScore }}</p>
            <p>Sets won: {{ player.setsWon }} / {{ gameSettings.setsToWin }}</p>
          </div>
          <div v-if="player.dartsThrown > 0" class="absolute bottom-0 inset-x-0 w-100 h-8">
            <div :title="'Hits: ' + player.dartsHit" v-text="player.dartsHit"
                 class="absolute left-0 inset-y-0 bg-success text-white overflow-clip flex items-center justify-center"
                 :style="{width: ((player.dartsHit / player.dartsThrown) * 100) + '%'}"></div>
            <div :title="'Misses: ' + (player.dartsThrown - player.dartsHit)"
                 v-text="player.dartsThrown - player.dartsHit"
                 class="absolute text-white right-0 inset-y-0 bg-error overflow-clip flex items-center justify-center"
                 :style="{width: ((1 - (player.dartsHit / player.dartsThrown)) * 100) + '%'}"></div>
          </div>
        </div>
      </div>

      <div v-if="gameOver" class="flex flex-wrap gap-3">
        <button
            @click="resetGame(true)"
            class="btn btn-success grow"
        >
          Reset Game
        </button>
        <button
            @click="leave"
            class="btn btn-error grow"
        >
          Back to Lobby
        </button>
      </div>
      <button v-else class="btn btn-success w-full" @click="resetGame">Next Set</button>
    </div>
  </div>
</template>

<script>
export default {
  name: "CricketGame",
  props: {
    socket: Object,
    thisUserId: String,
    gamePlayers: Object,
    gameSettings: Object,
  },
  data() {
    return {
      // Mapping gamePlayers by socketId
      players: Object.values(this.gamePlayers).map(player => ({
        id: player.id,
        name: player.name,
        inviter: player.inviter,
        hits: {},
        currentHits: {},
        totalScore: 0,
        dartsThrown: 0,
        dartsHit: 0,
        doubles: 0,
        triples: 0,
        highestThreeDartScore: 0,
        currentTurnScore: [],
        setsWon: 0,
        bot: player.bot ?? false
      })),
      currentPlayer: this.gamePlayers[0],
      numbers: this.generateCricketNumbers(this.gameSettings.cricketTill ?? 15),
      setsToWin: this.gameSettings.setsToWin ?? 3,
      round: 1,
      gameEnd: false,
      gameOver: false,
      winner: null,
      timer: 0,
      intervalId: null,
      history: [],
      hideStats: true,
    };
  },
  methods: {
    play(number, multiplier, bot = false) {
      const player = this.players.find(p => p.id === this.currentPlayer.id);

      if (!bot && this.currentPlayer.id !== this.thisUserId) {
        return;
      }

      if (player.currentTurnScore.length >= 3) {
        this.$showToast(`3 darts are thrown 🎯`, false);
        return;
      }

      if (!bot) {
        this.pushHistory();
      }

      const opponent = this.players.find(p => p.id !== this.currentPlayer.id);

      if (!player.hits[number]) player.hits[number] = 0;
      player.hits[number] += multiplier;

      if (!player.currentHits[multiplier]) {
        player.currentHits[multiplier] = [];
      }

      player.currentHits[multiplier].push(number);

      if (multiplier === 2) player.doubles++;
      if (multiplier === 3) player.triples++;

      if (player.hits[number] > 3) {
        const extraHits = player.hits[number] - 3;
        if (!opponent.hits[number] || opponent.hits[number] < 3) {
          player.totalScore += extraHits * (number === "Bull" ? 25 : number);
        }
        player.hits[number] = 3;
      }

      player.currentTurnScore.push(multiplier * (number === "Bull" ? 25 : number));
      player.dartsThrown++;
      player.dartsHit++;

      // Ensure Vue reacts to changes
      this.players = [...this.players];

      // Only emit socket update if this is a human player
      if (!bot) {
        this.socket.emit("scoreUpdate", { players: this.players });
      }

      this.checkGameEnd(player);
    },
    nextPlayer() {
      const player = this.players.find(p => p.id === this.currentPlayer.id);
      player.dartsThrown = this.round * 3;

      while (player.currentTurnScore.length < 3) {
        player.currentTurnScore.push(0);
      }

      const turnTotal = player.currentTurnScore.reduce((sum, dart) => sum + dart, 0);
      if (turnTotal > player.highestThreeDartScore) {
        player.highestThreeDartScore = turnTotal;
      }

      player.currentTurnScore = [];
      const nextPlayerIndex = this.players.findIndex(p => p.id !== this.currentPlayer.id);
      this.currentPlayer = this.players[nextPlayerIndex];

      if (this.currentPlayer.id === this.players[0].id) this.round++;

      this.players[nextPlayerIndex].currentHits = {};

      // Check if the next player is a bot
      if (this.currentPlayer.bot) {
        this.botPlay();
      }

      if (!this.currentPlayer.bot) {
        this.pushHistory();
      }

      this.socket.emit('nextPlayer', {
        history: this.history,
        players: this.players,
        currentPlayer: this.currentPlayer,
        round: this.round,
      });
    },
    botPlay() {
      let dartsThrown = 0;

      // Bot hit chances based on difficulty
      const difficultyConfig = {
        "easy": { accuracy: 0.3, multipliers: { single: 0.70, double: 0.20, triple: 0.10 } },
        "medium": { accuracy: 0.5, multipliers: { single: 0.60, double: 0.20, triple: 0.20 } },
        "hard": { accuracy: 0.7, multipliers: { single: 0.40, double: 0.30, triple: 0.30 } },
        "pro": { accuracy: 0.9, multipliers: { single: 0.30, double: 0.20, triple: 0.40 } }
      };

      const { accuracy, multipliers } = difficultyConfig[this.gameSettings.botDifficulty] || difficultyConfig["medium"];

      // Helper function to choose a number (either for points or to close a number)
      const getTargetNumber = () => {
        let targetNumber;

        // 50% chance to aim for points, 50% chance to close a number
        if (Math.random() < 0.5) {
          // Make sure to choose a valid, open number that the bot can hit
          const validNumbers = this.numbers.filter(num => !this.isClosed(num));
          if (validNumbers.length > 0) {
            targetNumber = validNumbers[Math.floor(Math.random() * validNumbers.length)];
          }
        }

        // If no valid target found for points, aim to close a number
        if (!targetNumber) {
          targetNumber = this.numbers.find(num =>
              this.players.find(p => p.id === this.currentPlayer.id).hits[num] < 3
          );
        }

        return targetNumber;
      };

      // Helper function to determine the multiplier based on difficulty
      const getMultiplier = () => {
        const rand = Math.random();
        if (rand < multipliers.triple) return 3;
        if (rand < multipliers.triple + multipliers.double) return 2;
        return 1;
      };

      // Simulate throwing darts
      while (dartsThrown < 3) {
        if (Math.random() < accuracy) { // Bot hits based on difficulty
          const targetNumber = getTargetNumber();
          if (targetNumber) {
            const multiplier = getMultiplier();
            console.log(`Bot (${this.gameSettings.botDifficulty}) throwing at ${targetNumber} with multiplier ${multiplier}`);
            this.play(targetNumber, multiplier, true);
          }
        } else {
          console.log(`Bot (${this.gameSettings.botDifficulty}) missed a throw`);
        }
        dartsThrown++;
      }

      // Move to the next player after a short delay
      setTimeout(() => this.nextPlayer(), 1000);
    },
    nextPlayerAction() {
      if (this.currentPlayer.id === this.thisUserId) {
        this.nextPlayer();
      }
    },
    undoPlayerAction() {
      if (this.currentPlayer.id === this.thisUserId || this.currentPlayer.bot) {
        this.undo();
      }
    },
    undo() {
      if (this.history.length > 1) {
        this.history.pop();
        // console.log('prev - ', this.history[this.history.length - 1])
        this.socket.emit('undo', {history: this.history});
      }
    },
    pushHistory() {
      if (this.history.length >= 5) {
        this.history.shift();
      }

      this.history.push({
        players: this.players,
        currentPlayer: this.currentPlayer,
        round: this.round,
      });
    },
    checkGameEnd(player) {
      const allClosed = this.numbers.every(num => player.hits[num] >= 3);
      const opponent = this.players.find(p => p.id !== player.id);

      if (allClosed && player.totalScore >= opponent.totalScore) {
        this.socket.emit('endGame', {winnerIndex: player.id});

        clearInterval(this.intervalId);
      }
    },
    isClosed(number) {
      // Check if all players have at least 3 hits for the given number
      return this.players.every(player => player.hits[number] >= 3);
    },
    resetGame(hard) {
      this.socket.emit('resetGame', {hard});
    },
    startTimer() {
      this.intervalId = setInterval(() => {
        this.timer++;
      }, 1000);
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    getHitsIcon(score) {
      if (score === 0) return;

      switch (score) {
        case 1:
          return '<img src="/assets/score/1.png" alt="1" class="max-sm:size-5 size-7.5"/>'
        case 2:
          return '<img src="/assets/score/2.png" alt="2" class="max-sm:size-5 size-7.5"/>'
        case 3:
          return '<img src="/assets/score/3.png" alt="3" class="max-sm:size-5 size-7.5"/>'
      }
    },
    formatHits(hits) {
      const labels = {"1": "Single", "2": "Double", "3": "Triple"};
      const entries = Object.entries(hits).map(([multiplier, numbers]) => {
        const label = labels[multiplier] || `x${multiplier}`; // Default to "xN" if multiplier is missing
        return `${label} ${numbers.sort((a, b) => a - b).join(', ')}`;
      });

      return entries.length > 1
          ? entries.slice(0, -1).join(', ') + " and " + entries[entries.length - 1]
          : entries[0] || "";
    },
    generateCricketNumbers(cricketTill) {
      const numbers = [];
      for (let i = 20; i >= cricketTill; i--) {
        numbers.push(i);
      }
      numbers.push("Bull"); // Always include "Bull" at the end
      return numbers;
    },
    leave() {
      // this.$router.push({ name: 'PlayerLobby' });
    }
  },
  mounted() {
    this.history.push({
      players: Object.values(this.gamePlayers).map(player => ({
        id: player.id,
        name: player.name,
        inviter: player.inviter,
        hits: {},
        currentHits: {},
        totalScore: 0,
        dartsThrown: 0,
        dartsHit: 0,
        doubles: 0,
        triples: 0,
        highestThreeDartScore: 0,
        currentTurnScore: [],
        setsWon: 0
      })),
      currentPlayer: this.gamePlayers[0],
      round: 1,
    });

    this.startTimer();

    this.socket.on('scoreUpdate', (data) => {
      this.players = data.players; // Update the players data
    });

    this.socket.on('undo', (data) => {
      if (data.history) {
        const lastHistory = data.history[data.history.length - 1];

        this.players = lastHistory.players;
        this.currentPlayer = lastHistory.currentPlayer;
        this.round = lastHistory.round;

        this.history = data.history;
      }
    });

    this.socket.on('endGame', (data) => {
      this.gameEnd = true;
      this.winner = this.players.find(p => p.id === data.winnerIndex);
      this.players.find(p => p.id === data.winnerIndex).setsWon++;

      this.gameOver = this.winner.setsWon >= this.gameSettings.setsToWin;
    });

    this.socket.on('resetGame', (data) => {
      // Find current starting player index
      const currentIndex = this.players.findIndex(p => p.id === this.currentPlayer.id);

      // Determine next starting player (alternate)
      const nextIndex = (currentIndex + 1) % this.players.length;

      // Reset players' stats
      this.players = Object.values(this.gamePlayers).map(player => ({
        id: player.id,
        name: player.name,
        inviter: player.inviter,
        hits: {},
        currentHits: {},
        totalScore: 0,
        dartsThrown: 0,
        dartsHit: 0,
        doubles: 0,
        triples: 0,
        highestThreeDartScore: 0,
        currentTurnScore: [],
        setsWon: data.hard ? 0 : this.players.find(p => p.id === player.id).setsWon,
        bot: player.bot ?? false,
      }));

      // Set new starting player
      this.currentPlayer = this.players[nextIndex];
      this.round = 1;
      this.gameEnd = false;
      this.winner = null;
      this.timer = 0;

      this.startTimer();

      // If the next player is a bot, trigger the bot to play immediately
      if (this.currentPlayer.bot) {
        this.botPlay();
      }
    });

    this.socket.on('nextPlayer', (data) => {
      this.players = data.players;
      this.currentPlayer = data.currentPlayer;
      this.round = data.round;
      this.history = data.history;

      const opponent = this.players.find(p => p.id !== this.currentPlayer.id);
      console.log('currentHits', opponent.currentHits, Object.keys(opponent.currentHits).length);

      const hitsCount = Object.keys(opponent.currentHits).length;
      this.$showToast(hitsCount ? `${opponent.name} hit ${this.formatHits(opponent.currentHits || {})}` : `${opponent.name} missed`, hitsCount);
    });
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
};

</script>
